import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import { dashboardState } from "../../../redux/reducers/dashboardReducer";
import { clearLoadingData } from "../../../redux/reducers/loadingReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { FcModal, Icon } from "../../classes/schedule/ui";
import { clubIdOf, useClub } from "./portalUtils";

/**
 * Member Schedule (new design). Same data and booking flow as the old ConsumersScheduleClassList / ConsumersSessionClassTile:
 *  - sessions: GET getMyClubSchedule?searchDate=<week start>&businessId=<club>&daysbeforeCurrentDate=0&daysAfterCurrentDate=7
 *  - classes (colours + filter): GET listOfClasses?…&businessId=<club>
 *  - Book: a free class (single price 0, single booking allowed) books directly after a confirm;
 *    otherwise GET checkApplicablePasses → no pass: send to Webshop; all credits used: message; else confirm →
 *    POST addClassBooking with the pass voucher. Bodies are exactly the old ones.
 * A session the member is already in (participants[].userId === me) shows "Booked".
 */

const CLASS_COLORS = ["#C8243A", "#481B3A", "#6B00BD", "#155E75", "#9A3412", "#3F6212", "#9D174D", "#3730A3"];
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const pad = (n) => String(n).padStart(2, "0");
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const colorFor = (classId) => {
  const str = String(classId || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return CLASS_COLORS[h % CLASS_COLORS.length];
};
const bookedCount = (s) => (Array.isArray(s.participants) ? s.participants : []).reduce((n, p) => n + (parseInt(p?.bookingQty, 10) || 1), 0);

function Thumb({ src, name, color, dim }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);
  const box = classNames("h-12 w-12 shrink-0 overflow-hidden rounded-2xl sm:h-14 sm:w-14", dim ? "opacity-60 grayscale" : "");
  if (src && !failed) {
    return (
      <span className={box}>
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      </span>
    );
  }
  return (
    <span className={classNames(box, "flex items-center justify-center text-lg font-black text-white")} style={{ background: color }}>
      {String(name || "C").slice(0, 1).toUpperCase()}
    </span>
  );
}

const Pill = ({ tone, children }) => (
  <span
    className={classNames(
      "inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold",
      tone === "green" ? "bg-[#E8F7EE] text-[#1F6B3A]" : tone === "red" ? "bg-[#FFE8EB] text-[#B4232F]" : "bg-[#F1EEF2] text-gray-600"
    )}
  >
    {children}
  </span>
);

export default function MemberSchedulePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addSuccessToast } = useToast();
  const userReduxState = useSelector(userState);
  const dashboardReduxState = useSelector(dashboardState);
  const club = useClub();
  const me = userReduxState.userData?._id;
  const clubId = clubIdOf(userReduxState.userData);

  const today = new Date();
  const [weekStart, setWeekStart] = React.useState(() => startOfWeek(new Date()));
  const [selected, setSelected] = React.useState(() => new Date());
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [classList, setClassList] = React.useState([]);
  const [filter, setFilter] = React.useState("");
  const [dlg, setDlg] = React.useState(null); // {kind, session, pass?, left?, message?}
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    dispatch(clearLoadingData()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!clubId) return;
    (async () => {
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForClasses()}listOfClasses?isPackage=false&listFor=${userReduxState.businessType || "class"}&onlyClassDetail=true&businessId=${clubId}`,
        });
        if (res.status === 200 && Array.isArray(res.data?.data)) setClassList(res.data.data.filter((c) => !c.isDeleted));
      } catch (e) {
        /* colours fall back to a hash */
      }
    })(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const reqRef = React.useRef(0);
  const loadWeek = React.useCallback(async () => {
    if (!clubId) return;
    const req = ++reqRef.current;
    setLoading(true);
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses("")}getMyClubSchedule?searchDate=${weekStart.toISOString()}&businessId=${clubId}&daysbeforeCurrentDate=0&daysAfterCurrentDate=7`,
      });
      if (req !== reqRef.current) return;
      if (res.status === 200 && res.data?.statusCode === 200) {
        const data = res.data.data || {};
        const flat = Array.isArray(data) ? data : Object.values(data).flat();
        const from = weekStart.getTime();
        const to = addDays(weekStart, 7).getTime();
        const seen = {};
        setSessions(
          flat
            .filter((s) => {
              if (!s || !s.classDate || seen[s._id]) return false;
              seen[s._id] = true;
              const t = new Date(s.classDate).getTime();
              return t >= from && t < to;
            })
            .sort((a, b) => new Date(a.classDate) - new Date(b.classDate))
        );
      } else setSessions([]);
    } catch (e) {
      if (req === reqRef.current) setSessions([]);
    } finally {
      if (req === reqRef.current) setLoading(false);
    }
  }, [clubId, weekStart]);

  React.useEffect(() => {
    loadWeek();
  }, [loadWeek, dashboardReduxState.updateSessionComp]);

  const colorIndex = {};
  classList.forEach((c, i) => {
    colorIndex[String(c._id)] = i;
  });
  const colorOf = (id) => (colorIndex[String(id)] !== undefined ? CLASS_COLORS[colorIndex[String(id)] % CLASS_COLORS.length] : colorFor(id));
  const photoOf = (s) => s.classPhoto || classList.find((c) => String(c._id) === String(s.classId))?.classPhoto || "";

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hasOn = (d) => sessions.some((s) => sameDay(new Date(s.classDate), d));
  const filterClasses = [];
  sessions.forEach((s) => {
    if (s.classId && !filterClasses.some((c) => String(c.id) === String(s.classId))) filterClasses.push({ id: s.classId, name: s.className });
  });
  const daySessions = sessions.filter((s) => sameDay(new Date(s.classDate), selected) && (!filter || String(s.classId) === String(filter)));
  const isThisWeek = sameDay(startOfWeek(today), weekStart);
  const weekEnd = addDays(weekStart, 6);
  const weekLabel =
    weekStart.getMonth() === weekEnd.getMonth()
      ? `${weekStart.getDate()} – ${weekEnd.getDate()} ${MON_SHORT[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`
      : `${weekStart.getDate()} ${MON_SHORT[weekStart.getMonth()]} – ${weekEnd.getDate()} ${MON_SHORT[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;

  const goWeek = (n) => {
    const w = addDays(weekStart, 7 * n);
    setWeekStart(w);
    setSelected(sameDay(startOfWeek(today), w) ? new Date() : w);
  };
  const goToday = () => {
    setWeekStart(startOfWeek(new Date()));
    setSelected(new Date());
  };

  /* ---------------- booking (same requests as the old tile) ---------------- */
  const user = userReduxState.userData || {};
  const baseBody = (item) => ({
    scheduleClassId: item._id,
    type: "add",
    isPackage: false,
    isEvent: false,
    username: user.username,
    fullName: user.fullName,
    email: user.email || "",
    isPaid: true,
    selectedEventPricing: [
      {
        bookingQty: 1,
        totalPrice: Number(item?.eventPricingList?.[0]?.amount || 0),
        ticketType: item?.eventPricingList?.[0]?.ticketType || "Single Session",
        serviceFee: 0,
        ticketAmount: Number(item?.eventPricingList?.[0]?.amount || 0),
      },
    ],
  });

  const startBooking = async (item) => {
    const singlePrice = Number(item?.eventPricingList?.[0]?.amount || 0);
    const singleBookingAllowed = item?.allowSingleSessionPurchase !== false;
    if (singleBookingAllowed && singlePrice === 0) {
      setDlg({ kind: "free", session: item });
      return;
    }
    setBusy(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}checkApplicablePasses?classId=${item.classId}&consumerId=${me}` });
      if (res.status !== 200) return setDlg({ kind: "error", session: item, message: "Couldn't check your passes. Please try again." });
      if (res.data.statusCode === 403) return setDlg({ kind: "error", session: item, message: res.data.message || "Couldn't check your passes." });
      const data = res.data.data || {};
      if (res.data.message === "No relevant passes or substitutions found" || Object.keys(data).length === 0) {
        return setDlg({ kind: "noPass", session: item });
      }
      const allowed = data?.passDetail?.noOfAllowedSessions;
      if (allowed && Number(allowed) && data.total_voucher_used >= Number(allowed)) {
        return setDlg({ kind: "usedUp", session: item, pass: data.passDetail?.passName });
      }
      const left = allowed && Number(allowed) ? Number(allowed) - (parseInt(data.total_voucher_used, 10) || 0) : null;
      setDlg({ kind: "pass", session: item, pass: data.passDetail?.passName || "pass", left, voucher: data.passVoucherCode });
    } catch (e) {
      setDlg({ kind: "error", session: item, message: "Couldn't reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const confirmBooking = async () => {
    const item = dlg.session;
    const body =
      dlg.kind === "free"
        ? { ...baseBody(item), bookingId: "", bookingsReservationId: "", paymentMethod: "NA", paymenthMode: "NA", paymentStatus: "Paid" }
        : { ...baseBody(item), paymentMethod: "classPass", paymentMode: "Online", paymentStatus: "Paid", passVoucherCode: dlg.voucher };
    setBusy(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings("")}addClassBooking`, data: body });
      if (res.status === 200 && res.data.statusCode === 200) {
        setDlg(null);
        addSuccessToast(`You're booked for ${item.className}. Enjoy the class!`);
        dispatch(dashboardActions.setUpdateSessionComp(dashboardReduxState.updateSessionComp + 1));
      } else {
        setDlg({ kind: "error", session: item, message: (typeof res.data?.message === "string" && res.data.message) || "Something went wrong. Please try again." });
      }
    } catch (e) {
      setDlg({ kind: "error", session: item, message: "Couldn't reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const when = (s) => {
    if (!s) return "";
    const a = new Date(s.classDate);
    const b = s.classEndDate ? new Date(s.classEndDate) : null;
    return `${DAY_LONG[a.getDay()].slice(0, 3)} ${a.getDate()} ${MON_SHORT[a.getMonth()]} · ${hhmm(a)}${b ? `–${hhmm(b)}` : ""}`;
  };

  const dlgProps = (() => {
    if (!dlg) return null;
    const name = dlg.session?.className || "this class";
    switch (dlg.kind) {
      case "free":
        return { title: `Book ${name}?`, primaryLabel: "Book now", onPrimary: confirmBooking, body: <p className="text-sm text-gray-700">This class is free. Your spot is saved straight away.</p> };
      case "pass":
        return {
          title: `Book ${name}?`,
          primaryLabel: "Book now",
          onPrimary: confirmBooking,
          body: (
            <div className="flex items-center gap-3 rounded-2xl bg-[#F7F4F8] px-4 py-3.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E9FF] font-black text-[#6A00BA]">1</span>
              <div className="text-sm text-gray-800">
                <b>Uses 1 credit</b> of your {dlg.pass}
                {dlg.left !== null && (
                  <span className="block text-gray-500">
                    {dlg.left} left now, {Math.max(0, dlg.left - 1)} after booking
                  </span>
                )}
              </div>
            </div>
          ),
        };
      case "noPass":
        return {
          title: `You need a pass for ${name}`,
          primaryLabel: "Go to Club Webshop",
          onPrimary: () => {
            setDlg(null);
            navigate("/members/webshop");
          },
          body: <p className="text-sm text-gray-700">You don't have a pass or subscription that covers this class. Buy one in the Club Webshop, then come back to book.</p>,
        };
      case "usedUp":
        return {
          title: "No credits left",
          primaryLabel: "Go to Club Webshop",
          onPrimary: () => {
            setDlg(null);
            navigate("/members/webshop");
          },
          body: <p className="text-sm text-gray-700">All credits of your {dlg.pass || "pass"} are used. Buy a new pass in the Club Webshop to book this class.</p>,
        };
      default:
        return { title: "Couldn't book this class", primaryLabel: "OK", onPrimary: () => setDlg(null), body: <p className="text-sm text-gray-700">{dlg.message}</p> };
    }
  })();

  return (
    <TwMainLayout
      userType={"student"}
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="mx-auto flex max-w-6xl flex-col gap-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">Club Schedule</h1>
                <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">Book your classes at {club.name}.</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" aria-label="Previous week" onClick={() => goWeek(-1)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  <Icon d={<path d="m15 18-6-6 6-6" />} />
                </button>
                <span className="min-w-[9.5rem] text-center text-[15px] font-extrabold text-gray-900">{weekLabel}</span>
                <button type="button" aria-label="Next week" onClick={() => goWeek(1)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  <Icon d={<path d="m9 18 6-6-6-6" />} />
                </button>
                {!isThisWeek && (
                  <button type="button" onClick={goToday} className="h-10 rounded-xl border border-gray-200 bg-white px-3.5 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                    Today
                  </button>
                )}
              </div>
            </div>

            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
              <div className="grid min-w-[560px] grid-cols-7 gap-2">
                {days.map((d) => {
                  const on = sameDay(d, selected);
                  const past = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  return (
                    <button
                      key={d.toISOString()}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setSelected(d)}
                      className={classNames(
                        "flex h-[72px] flex-col items-center justify-center gap-0.5 rounded-2xl border",
                        on ? "border-brown-coffee bg-brown-coffee text-white" : "border-[#ECE6EE] bg-white text-gray-900 hover:bg-[#FAF8FB]",
                        past && !on ? "opacity-60" : ""
                      )}
                    >
                      <span className={classNames("text-xs font-extrabold", on ? "text-white/80" : "text-gray-500")}>{sameDay(d, today) ? "TODAY" : DOW[d.getDay()]}</span>
                      <span className="text-[22px] font-black leading-none">{d.getDate()}</span>
                      <span className={classNames("h-1.5 w-1.5 rounded-full", hasOn(d) ? (on ? "bg-[#F7C6CC]" : "bg-portland-orange") : "bg-transparent")} />
                    </button>
                  );
                })}
              </div>
            </div>

            {filterClasses.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {[{ id: "", name: "All classes" }, ...filterClasses].map((c) => {
                  const on = String(filter) === String(c.id);
                  return (
                    <button
                      key={c.id || "all"}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setFilter(c.id)}
                      className={classNames("h-9 rounded-full border px-4 text-[13px] font-extrabold", on ? "border-brown-coffee bg-brown-coffee text-white" : "border-[#E3DCE6] bg-white text-gray-700 hover:bg-[#FAF8FB]")}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}

            <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
              <h2 className="px-4 pt-4 text-[13px] font-black tracking-wide text-gray-500 sm:px-5">
                {`${DAY_LONG[selected.getDay()]} ${selected.getDate()} ${MONTHS[selected.getMonth()]}`.toUpperCase()}
                {sameDay(selected, today) ? " · TODAY" : ""}
              </h2>
              {loading ? (
                <div className="flex flex-col gap-2 p-4 sm:p-5" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-20 animate-pulse rounded-2xl bg-[#F3EEF5]" />
                  ))}
                </div>
              ) : daySessions.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                    <Icon className="h-6 w-6" d={<><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />
                  </span>
                  <div className="text-base font-black text-gray-900">No classes on this day</div>
                  <div className="text-sm text-gray-500">{days.some(hasOn) ? "Pick another day this week." : "Nothing scheduled this week yet. Try next week."}</div>
                </div>
              ) : (
                <ul className="mt-2">
                  {daySessions.map((s) => {
                    const start = new Date(s.classDate);
                    const end = s.classEndDate ? new Date(s.classEndDate) : null;
                    const mins = end ? Math.round((end - start) / 60000) : 0;
                    const cap = parseInt(s.capacity, 10) || 0;
                    const booked = bookedCount(s);
                    const left = cap ? Math.max(0, cap - booked) : null;
                    const mine = (s.participants || []).some((p) => String(p?.userId) === String(me));
                    const cancelled = String(s.eventStatus || "").toLowerCase() === "cancelled";
                    const ended = (end || start) < new Date();
                    const full = cap > 0 && left === 0;
                    const pct = cap ? Math.min(100, Math.round((booked / cap) * 100)) : 0;
                    const color = colorOf(s.classId);
                    const where = s.location?.addressName || [s.location?.houseNumber, s.location?.city].filter(Boolean).join(", ");
                    const action = mine ? (
                      <button type="button" onClick={() => navigate("/members/bookings")} title="See your bookings" className="rounded-xl">
                        <Pill tone="green">
                          <Icon className="h-4 w-4" d={<path d="M5 12l5 5L20 7" />} />
                          Booked
                        </Pill>
                      </button>
                    ) : cancelled ? (
                      <Pill tone="red">Cancelled</Pill>
                    ) : ended ? (
                      <span className="inline-flex h-10 items-center px-2 text-sm font-extrabold text-gray-500">Ended</span>
                    ) : full ? (
                      <Pill>Full</Pill>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => startBooking(s)}
                        className="h-10 rounded-xl bg-brown-coffee px-5 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
                      >
                        Book
                      </button>
                    );
                    return (
                      <li
                        key={s._id}
                        className={classNames(
                          "grid grid-cols-[52px_5px_minmax(0,1fr)] items-center gap-x-3 border-t border-gray-100 px-4 py-3.5 sm:grid-cols-[64px_6px_minmax(0,1fr)] sm:gap-x-4 sm:px-5 lg:grid-cols-[64px_6px_minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1.1fr)_120px]",
                          (ended && !mine) || cancelled ? "opacity-60" : ""
                        )}
                      >
                        <div>
                          <div className="text-base font-black text-gray-900 sm:text-lg">{hhmm(start)}</div>
                          {mins > 0 && <div className="text-xs font-bold text-gray-500">{mins} min</div>}
                        </div>
                        <span className="h-12 w-[5px] rounded-full sm:w-1.5" style={{ background: color }} aria-hidden="true" />
                        <div className="flex min-w-0 items-center gap-3">
                          <Thumb src={photoOf(s)} name={s.className} color={color} dim={cancelled} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[15px] font-extrabold text-gray-900 sm:text-base">{s.className}</div>
                            <div className="truncate text-[13px] text-gray-500">
                              {s.instructorName ? `with ${s.instructorName}` : ""}
                              <span className="lg:hidden">
                                {s.instructorName && where ? " · " : ""}
                                {where}
                                {left !== null && !ended && !cancelled ? ` · ${full ? "Full" : `${left} ${left === 1 ? "spot" : "spots"} left`}` : ""}
                              </span>
                            </div>
                          </div>
                          <div className="lg:hidden">{action}</div>
                        </div>
                        <div className="hidden min-w-0 text-[13px] font-bold text-gray-700 lg:block">
                          <span className="block truncate">{where || "–"}</span>
                        </div>
                        <div className="hidden lg:block">
                          {cap > 0 ? (
                            <>
                              <div className="text-xs font-extrabold text-gray-700">{full ? "Full" : `${left} of ${cap} spots left`}</div>
                              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EDE5F0]">
                                <div className={classNames("h-full rounded-full", full ? "bg-portland-orange" : "bg-indigo-bz")} style={{ width: `${pct}%` }} />
                              </div>
                            </>
                          ) : (
                            <span className="text-xs font-bold text-gray-500">Open</span>
                          )}
                        </div>
                        <div className="hidden justify-end lg:flex">{action}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>

          <FcModal
            open={!!dlg}
            onClose={() => !busy && setDlg(null)}
            eyebrow="BOOK A CLASS"
            title={dlgProps?.title || ""}
            subtitle={dlg ? `${when(dlg.session)} · ${dlg.session?.location?.addressName || club.name}` : ""}
            primaryLabel={dlgProps?.primaryLabel || "OK"}
            onPrimary={dlgProps?.onPrimary}
            busy={busy}
          >
            {dlgProps?.body}
          </FcModal>
        </div>
      }
    />
  );
}
